import { useMemo, useState, useEffect } from "react";
import { toast } from "sonner";

/**
 * Preset + custom date range filter for admin analytics.
 * Emits {range: "7d"|"30d"|"90d"|"custom", from?: "YYYY-MM-DD", to?: "YYYY-MM-DD"}.
 * Dates are Asia/Riyadh calendar days (backend converts to UTC).
 */
export default function DateRangeFilter({ value, onChange, testidPrefix = "range" }) {
  const [tempFrom, setTempFrom] = useState(value?.from || "");
  const [tempTo, setTempTo] = useState(value?.to || "");

  useEffect(() => {
    setTempFrom(value?.from || "");
    setTempTo(value?.to || "");
  }, [value?.from, value?.to]);

  const todayStr = useMemo(() => {
    // Riyadh "today" — offset +3h from UTC.
    const d = new Date(Date.now() + 3 * 3600 * 1000);
    return d.toISOString().slice(0, 10);
  }, []);

  const applyCustom = () => {
    if (!tempFrom || !tempTo) {
      toast.error("يرجى اختيار تاريخ البداية والنهاية");
      return;
    }
    if (tempTo < tempFrom) {
      toast.error("تاريخ النهاية يجب أن يكون بعد تاريخ البداية");
      return;
    }
    if (tempFrom > todayStr || tempTo > todayStr) {
      toast.error("لا يمكن اختيار تاريخ مستقبلي");
      return;
    }
    onChange({ range: "custom", from: tempFrom, to: tempTo });
  };

  const resetDefault = () => {
    setTempFrom("");
    setTempTo("");
    onChange({ range: "30d" });
  };

  return (
    <div
      data-testid={`${testidPrefix}-picker`}
      className="bg-white border-2 border-slate-200 rounded-2xl p-4 space-y-3"
    >
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-bold text-slate-500">النطاق الزمني:</label>
        <select
          data-testid={`${testidPrefix}-preset`}
          value={value?.range || "30d"}
          onChange={(e) =>
            onChange(
              e.target.value === "custom"
                ? { range: "custom", from: tempFrom, to: tempTo }
                : { range: e.target.value }
            )
          }
          className="bg-slate-50 border-2 border-slate-200 rounded-lg px-3 py-2 font-bold"
        >
          <option value="7d">آخر 7 أيام</option>
          <option value="30d">آخر 30 يوماً</option>
          <option value="90d">آخر 90 يوماً</option>
          <option value="custom">تحديد تاريخ مخصص</option>
        </select>
        {value?.range && value.range !== "30d" && (
          <button
            type="button"
            data-testid={`${testidPrefix}-reset`}
            onClick={resetDefault}
            className="text-sm font-bold bg-slate-100 hover:bg-slate-200 text-slate-800 px-3 py-2 rounded-lg cursor-pointer"
          >
            إعادة تعيين
          </button>
        )}
        <span className="text-xs text-slate-400 mr-auto">التواريخ بتوقيت الرياض</span>
      </div>

      {value?.range === "custom" && (
        <div
          data-testid={`${testidPrefix}-custom-inputs`}
          className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end"
        >
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">من</label>
            <input
              data-testid={`${testidPrefix}-from`}
              type="date"
              max={todayStr}
              value={tempFrom}
              onChange={(e) => setTempFrom(e.target.value)}
              className="w-full bg-slate-50 border-2 border-slate-200 rounded-lg px-3 py-2 font-bold"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">إلى</label>
            <input
              data-testid={`${testidPrefix}-to`}
              type="date"
              max={todayStr}
              value={tempTo}
              onChange={(e) => setTempTo(e.target.value)}
              className="w-full bg-slate-50 border-2 border-slate-200 rounded-lg px-3 py-2 font-bold"
            />
          </div>
          <button
            type="button"
            data-testid={`${testidPrefix}-apply`}
            onClick={applyCustom}
            className="bg-slate-900 hover:bg-slate-800 text-white font-bold px-4 py-2.5 rounded-lg cursor-pointer"
          >
            تطبيق
          </button>
        </div>
      )}
    </div>
  );
}
